import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Users, FileText, LogOut, ShieldCheck, 
  ChevronDown, PlusCircle, List, Edit, Settings, X,
  Award, createLucideIcon , GitPullRequest
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAdminTheme } from '../context/AdminThemeContext';

// Props Interface
interface AdminSidebarProps {
    isOpen: boolean;
    onClose: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { colors } = useAdminTheme();

  // State for Dropdowns
  const [isStudentMenuOpen, setIsStudentMenuOpen] = useState(false);
  const [isQuoteModalOpen , setIsQuoteModalOpen] = useState(false)

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/pit-control');
  };

  const isActive = (path: string) => location.pathname === path;

  // --- 3D Button Component ---
  const MenuButton = ({ icon: Icon, label, path, onClick, hasSubmenu = false, isOpen = false }: any) => {
    const active = isActive(path);
    return (
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {
            if(onClick) onClick();
            else {
                navigate(path);
                // On mobile, close sidebar after navigation
                if (window.innerWidth < 1024) onClose();
            }
        }}
        className={`
          relative w-full flex items-center justify-between p-3 mb-2 rounded-xl transition-all duration-200
          border-b-4 font-bold tracking-wide
          ${active 
            ? `${colors.button3D} text-white border-black/20 shadow-lg translate-y-[1px]` 
            : `${colors.sidebarBg} ${colors.sidebarText} hover:bg-white/10 border-transparent hover:border-black/10`
          }
        `}
      >
        <div className="flex items-center gap-3">
          <Icon size={20} className={active ? "animate-pulse" : ""} />
          <span>{label}</span>
        </div>
        {hasSubmenu && (
           <ChevronDown size={16} className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </motion.button>
    );
  };

  const SubMenuItem = ({ label, path, icon: Icon }: any) => (
    <motion.div
      whileHover={{ x: 5 }}
      onClick={() => {
          navigate(path);
          if (window.innerWidth < 1024) onClose();
      }}
      className={`
        flex items-center gap-3 p-2 pl-4 mx-2 mt-1 rounded-lg cursor-pointer text-sm font-medium opacity-80 hover:opacity-100 hover:bg-white/10 transition-colors
        ${isActive(path) ? 'text-white bg-white/20' : colors.sidebarText}
      `}
    >
       <Icon size={14} />
       {label}
    </motion.div>
  );

  return (
    <>
    {/* CSS to Hide Scrollbar */}
    <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
    `}</style>

    <aside 
        className={`
            fixed top-0 left-0 h-screen w-64 z-50 shadow-2xl transition-transform duration-300 ease-in-out flex flex-col
            ${colors.sidebarBg} 
            ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
    >
      
      {/* 1. Header Area */}
      <div className="h-20 flex items-center justify-between px-6 border-b border-white/10 relative overflow-hidden shrink-0">
        <div className="absolute inset-0 bg-white/5 opacity-0 hover:opacity-100 transition-opacity"></div>
        <div className="flex items-center gap-2 text-white font-extrabold text-2xl tracking-widest z-10">
            <ShieldCheck size={28} className="text-yellow-400 drop-shadow-md" />
            <span>PIT<span className="text-yellow-400">ADMIN</span></span>
        </div>
        {/* Mobile Close Button */}
        <button onClick={onClose} className="lg:hidden text-white/70 hover:text-white z-20">
            <X size={24} />
        </button>
      </div>
      
      {/* 2. Navigation (With No Scrollbar Class) */}
      <nav className="flex-1 p-4 overflow-y-auto no-scrollbar">
        
        <MenuButton icon={LayoutDashboard} label="Dashboard" path="/admin/dashboard" />

        {/* Students Dropdown */}
        <MenuButton 
          icon={Users} 
          label="Students" 
          path="#" 
          onClick={() => setIsStudentMenuOpen(!isStudentMenuOpen)}
          hasSubmenu={true} 
          isOpen={isStudentMenuOpen} 
        />
        
        <AnimatePresence>
          {isStudentMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden bg-black/20 rounded-xl mb-2"
            >
              <div className="p-2 space-y-1">
                 <SubMenuItem label="Add Student" path="/admin/students/add" icon={PlusCircle} />
                 <SubMenuItem label="Student List" path="/admin/students" icon={List} />
                 <SubMenuItem label="Edit Student" path="/admin/students/edit" icon={Edit} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <MenuButton icon={FileText} label="Queries" path="/admin/queries" />
        <MenuButton icon={FileText} label="Syllabus" path="/admin/syllabus" />
        <MenuButton icon={FileText} label="Notes" path="/admin/notes" />
        <MenuButton icon={FileText} label="Interview Files" path="/admin/interview-files" />
       <MenuButton icon={Award} label="Certificate" path="/admin/certificate" />
       <MenuButton icon={Award} label="Ad-Enquiry" path="/admin/ad-enquiry" />


        {/* <MenuButton icon={Settings} label="Settings" path="/admin/settings" /> */}
           <MenuButton 
          icon={Users} 
          label="Quotation" 
          path="#" 
          // onClick={() => setIsStudentMenuOpen(!isStudentMenuOpen)}
          onClick={()=> setIsQuoteModalOpen(!isQuoteModalOpen)}
          hasSubmenu={true} 
          isOpen={isStudentMenuOpen} 
        />
        
        <AnimatePresence>
          {isQuoteModalOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden bg-black/20 rounded-xl mb-2"
            >
              <div className="p-2 space-y-1">
                 <SubMenuItem label="User Requests" path="/admin/quotation/request" icon={PlusCircle} />
                 <SubMenuItem label="Create Quotation" path="/admin/quotation/create-quote" icon={List} />
                 <SubMenuItem label="History" path="/admin/quotation/history" icon={Edit} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </nav>

      {/* 3. Footer / Logout */}
      <div className="p-4 border-t border-white/10 bg-black/10 shrink-0">
        <button 
          onClick={handleLogout}
          className="group flex items-center justify-center space-x-2 w-full p-3 rounded-xl bg-red-600/20 hover:bg-red-600 text-red-200 hover:text-white transition-all duration-300 border border-red-500/30 hover:border-red-500"
        >
          <LogOut size={18} className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-semibold">Sign Out</span>
        </button>
        <p className="text-center text-[10px] text-white/30 mt-3">
            v1.0.2 &copy; PIT Institute
        </p>
      </div>
    </aside>
    </>
  );
};

export default AdminSidebar;